import React, { useEffect, useState } from "react";
import { Bell, X, Loader2, Check } from "lucide-react";
import api from "../lib/api";
import { pushSupported, enablePush } from "../lib/push";

const LS_DONE = "ss_push_enabled";
const SS_DISMISS = "ss_push_dismissed";

export const PushPrompt = () => {
  const [show, setShow] = useState(false);
  const [state, setState] = useState("idle"); // idle | loading | done

  useEffect(() => {
    if (!pushSupported()) return;
    if (Notification.permission !== "default") return; // already granted or denied
    if (localStorage.getItem(LS_DONE) === "1") return;
    if (sessionStorage.getItem(SS_DISMISS) === "1") return;
    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const allow = async () => {
    setState("loading");
    try {
      const res = await enablePush();
      if (res.ok) {
        localStorage.setItem(LS_DONE, "1");
        setState("done");
        try { await api.post("/push/test"); } catch (e) {}
        setTimeout(() => setShow(false), 2200);
      } else {
        // denied/unsupported, don't nag again this session
        sessionStorage.setItem(SS_DISMISS, "1");
        setShow(false);
      }
    } catch (e) {
      sessionStorage.setItem(SS_DISMISS, "1");
      setShow(false);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem(SS_DISMISS, "1");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+88px)] sm:pb-6 pointer-events-none" data-testid="push-prompt">
      <div className="pointer-events-auto w-full max-w-md bg-[#0F0F0F] border border-white/10 shadow-2xl shadow-black/60 p-5 fade-up relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#14A89A]/10 blur-2xl" />
        <button onClick={dismiss} data-testid="push-dismiss" className="absolute top-3 right-3 text-zinc-600 hover:text-white transition-colors"><X size={16} /></button>
        <div className="flex items-start gap-4 relative">
          <div className="shrink-0 w-11 h-11 rounded-full bg-[#14A89A]/15 border border-[#14A89A]/30 flex items-center justify-center">
            <Bell size={18} className="text-[#14A89A]" />
          </div>
          <div className="min-w-0">
            <h3 className="font-heading text-base text-white leading-tight">Stay in the signal</h3>
            <p className="font-body text-sm text-zinc-400 mt-1.5">
              Be notified when a sovereign friend reaches out, a message, a response, or appreciation for your work.
            </p>
            {state === "done" ? (
              <div className="flex items-center gap-2 text-[#14A89A] font-body text-sm mt-4" data-testid="push-enabled-confirm">
                <Check size={16} /> You're connected.
              </div>
            ) : (
              <div className="flex items-center gap-3 mt-4">
                <button onClick={allow} disabled={state === "loading"} data-testid="push-allow" className="bg-[#14A89A] text-[#0A0A0A] font-body text-sm font-medium px-5 py-2.5 hover:bg-[#2BC4B5] transition-colors flex items-center gap-2 disabled:opacity-60">
                  {state === "loading" ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} />}
                  Allow notifications
                </button>
                <button onClick={dismiss} data-testid="push-later" className="font-body text-sm text-zinc-500 hover:text-white transition-colors">Not now</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
